import type { ObjectData, ObjectId } from "@novorender/data-js-api";

/**
 * LRU cache of object metadata, shared by the backends that read it over the network
 * (`AppObjectDB`, `BimIndexDataAccess`) — a metadata read is by far the most repeated
 * request in the app, since every panel that opens on a selection makes one.
 */
export class ObjectMetadataCache {
    static readonly MAX_ENTRIES = 1000;
    /**
     * Property-heavy IFC objects can reach tens of KB of metadata each, so an
     * entry cap alone is not a byte bound; large entries shrink the effective
     * cache depth instead of growing the heap.
     */
    static readonly MAX_APPROX_BYTES = 8 * 1024 * 1024;

    /** Iteration order is LRU order: entries are re-inserted on access. */
    private cache = new Map<ObjectId, { promise: Promise<ObjectData | undefined>; approxBytes: number }>();
    private approxBytesTotal = 0;

    /**
     * The loader's own result type is preserved: a backend that can answer "no such
     * object" keeps its `undefined`, one that always throws instead stays non-optional.
     * An absence is not cached — it costs a single re-ask, and a backend can come to
     * know an object it did not have.
     */
    getOrLoadMetadata = async <T extends ObjectData | undefined>(
        objectId: ObjectId,
        load: (objectId: ObjectId) => Promise<T>,
    ): Promise<T> => {
        let cacheEntry = this.cache.get(objectId);

        if (cacheEntry) {
            this.cache.delete(objectId);
            this.cache.set(objectId, cacheEntry);
        } else {
            const promise = load(objectId);
            const entry = (cacheEntry = { promise, approxBytes: 0 });
            promise.then(
                (data) => {
                    if (this.cache.get(objectId) !== entry) {
                        return;
                    }
                    if (!data) {
                        this.cache.delete(objectId);
                        return;
                    }
                    entry.approxBytes = approxObjectDataBytes(data);
                    this.approxBytesTotal += entry.approxBytes;
                    this.evictOverBudget();
                },
                () => {
                    if (this.cache.get(objectId) === entry) {
                        this.cache.delete(objectId);
                    }
                },
            );
            this.cache.set(objectId, cacheEntry);
            this.evictOverBudget();
        }

        // One id is served by one backend, so the entry resolves to that loader's type.
        return (await cacheEntry.promise) as T;
    };

    /**
     * Seed the cache with metadata another read already produced, so the read this would
     * cost never happens — selection resolution climbs to an object and gets its
     * properties in the same request.
     *
     * A no-op for an id already here: one id is one object, so the entry holds the same
     * metadata or an in-flight read for it.
     */
    put = (objectId: ObjectId, data: ObjectData) => {
        if (this.cache.has(objectId)) {
            return;
        }
        const approxBytes = approxObjectDataBytes(data);
        this.cache.set(objectId, { promise: Promise.resolve(data), approxBytes });
        this.approxBytesTotal += approxBytes;
        this.evictOverBudget();
    };

    has = (objectId: ObjectId) => {
        return this.cache.has(objectId);
    };

    get stats() {
        return { entryCount: this.cache.size, approxBytes: this.approxBytesTotal };
    }

    private evictOverBudget = () => {
        while (
            this.cache.size > ObjectMetadataCache.MAX_ENTRIES ||
            this.approxBytesTotal > ObjectMetadataCache.MAX_APPROX_BYTES
        ) {
            const oldest = this.cache.keys().next();
            if (oldest.done) {
                break;
            }
            this.approxBytesTotal -= this.cache.get(oldest.value)!.approxBytes;
            this.cache.delete(oldest.value);
        }
    };
}

/** Rough JS-heap estimate: 2 bytes per UTF-16 code unit plus per-object/per-tuple overhead. */
function approxObjectDataBytes(data: ObjectData): number {
    let bytes = 200 + (data.name.length + data.path.length + (data.description?.length ?? 0)) * 2;
    for (const [key, value] of data.properties) {
        bytes += 64 + (key.length + value.length) * 2;
    }
    bytes += (data.descendants?.length ?? 0) * 8;
    return bytes;
}
